/**
 * LISP script library — persisted .lsp sources loaded through APPLOAD.
 * User preference state (persisted), NOT document state. The content itself
 * is stored so the startup suite can re-load scripts across sessions
 * (the sandboxed runtime has no host file-system access).
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';

export type LispScriptStatus = 'unloaded' | 'loaded' | 'error';

export interface LispScript {
    id: string;
    name: string;
    content: string;
    loadAtStartup: boolean;
    status: LispScriptStatus;
    message?: string;
    /** c: command names contributed by the last successful load. */
    commands: string[];
    loadedAt?: number;
}

/** Content size guard: localStorage-backed, keep a wide safety margin. */
const MAX_SCRIPT_CHARS = 1_500_000;

interface LispScriptsState {
    scripts: LispScript[];
    /** Returns the new script id, or null when the content is too large. */
    addScript: (name: string, content: string) => string | null;
    removeScript: (id: string) => void;
    setStatus: (id: string, status: LispScriptStatus, message?: string, commands?: string[]) => void;
    toggleStartup: (id: string) => void;
}

export const useLispScriptsStore = create<LispScriptsState>()(
    persist(
        (set) => ({
            scripts: [],
            addScript: (name, content) => {
                if (content.length > MAX_SCRIPT_CHARS) return null;
                const script: LispScript = {
                    id: uuidv4(),
                    name,
                    content,
                    loadAtStartup: false,
                    status: 'unloaded',
                    commands: [],
                };
                set((state) => ({ scripts: [...state.scripts, script] }));
                return script.id;
            },
            removeScript: (id) =>
                set((state) => ({ scripts: state.scripts.filter((s) => s.id !== id) })),
            setStatus: (id, status, message, commands) =>
                set((state) => ({
                    scripts: state.scripts.map((s) =>
                        s.id === id
                            ? {
                                  ...s,
                                  status,
                                  message,
                                  commands: commands ?? (status === 'loaded' ? s.commands : []),
                                  loadedAt: status === 'loaded' ? Date.now() : s.loadedAt,
                              }
                            : s
                    ),
                })),
            toggleStartup: (id) =>
                set((state) => ({
                    scripts: state.scripts.map((s) =>
                        s.id === id ? { ...s, loadAtStartup: !s.loadAtStartup } : s
                    ),
                })),
        }),
        { name: 'yap-lisp-scripts' }
    )
);
